/**
 * Mapa de origen de las solicitudes — dónde estaba el paciente al pedir la
 * teleconsulta.
 *
 * POR QUÉ VECTORIAL Y NO UN MAPA DE TESELAS. Un televisor de sala de operación
 * está encendido todo el día: un mapa de teselas (Leaflet + OpenStreetMap,
 * Google, Mapbox) pediría imágenes al servidor del proveedor sin parar, exige
 * clave y facturación en dos de los tres casos, se rompe si ese host no está
 * accesible desde la red del hospital, y se ve borroso al escalarlo a 55" o 75".
 * Este mapa dibuja los contornos como VECTORES empaquetados en la app: nítido a
 * cualquier tamaño, sin clave que configurar, sin red que dependa de terceros y
 * sin una sola petición externa. El detalle callejero que dan las teselas no
 * aporta nada a cinco metros de distancia.
 *
 * SIN DATOS DE PACIENTES. Cada punto es una solicitud, sin nombre ni motivo;
 * los puntos que coinciden se agrupan y se muestran con su conteo.
 */
import { useMemo } from 'react';
import s from './MapaOrigen.module.css';

export interface PuntoMapa {
  lat: number;
  lng: number;
  /** Etiqueta de la zona resuelta, para el título accesible. */
  zona?: string;
}

interface Contorno { n: string; p: [number, number][][][] }

/** Encuadre de Panamá continental + archipiélagos (lng/lat). */
const CAJA = { oeste: -83.2, este: -77.1, sur: 7.0, norte: 9.75 };

/**
 * Proyección Mercator. Panamá está cerca del ecuador, así que la distorsión es
 * mínima, pero usarla igual mantiene las formas fieles y coincide con lo que
 * cualquiera reconoce como "la forma del país".
 */
const mercY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 180 / 2));

const Y_SUR = mercY(CAJA.sur);
const Y_NORTE = mercY(CAJA.norte);
const ANCHO = 1000;
const ALTO = Math.round(
  (ANCHO * (Y_NORTE - Y_SUR)) / (((CAJA.este - CAJA.oeste) * Math.PI) / 180),
);

const px = (lng: number) => ((lng - CAJA.oeste) / (CAJA.este - CAJA.oeste)) * ANCHO;
const py = (lat: number) => ((Y_NORTE - mercY(lat)) / (Y_NORTE - Y_SUR)) * ALTO;

const dentroDeCaja = (lat: number, lng: number) =>
  lng >= CAJA.oeste && lng <= CAJA.este && lat >= CAJA.sur && lat <= CAJA.norte;

/**
 * Centroide de ÁREA de una anilla (no el promedio de vértices, que se sesga
 * hacia los tramos con más detalle, como las costas recortadas).
 */
function centroideArea(anilla: [number, number][]): [number, number] {
  let a = 0, cx = 0, cy = 0;
  for (let i = 0; i < anilla.length - 1; i++) {
    const [x0, y0] = anilla[i];
    const [x1, y1] = anilla[i + 1];
    const f = x0 * y1 - x1 * y0;
    a += f; cx += (x0 + x1) * f; cy += (y0 + y1) * f;
  }
  if (a === 0) return anilla[0];
  a *= 0.5;
  return [cx / (6 * a), cy / (6 * a)];
}

function areaAnilla(anilla: [number, number][]): number {
  let a = 0;
  for (let i = 0; i < anilla.length - 1; i++) {
    const [x0, y0] = anilla[i];
    const [x1, y1] = anilla[i + 1];
    a += x0 * y1 - x1 * y0;
  }
  return Math.abs(a / 2);
}

/**
 * Dos provincias tienen una forma que deja su centroide donde el rótulo
 * estorba: el de Panamá cae muy al este por el brazo hacia Darién, y el de
 * Guna Yala queda en una franja costera demasiado angosta para escribir
 * encima. Solo esas dos llevan posición fijada a mano.
 */
const POSICION_ROTULO: Record<string, [number, number]> = {
  'Panamá': [-79.05, 8.95],
  'Guna Yala': [-78.45, 9.45],
};

function trazaContorno(polis: [number, number][][][]): string {
  let d = '';
  for (const poly of polis) {
    for (const anilla of poly) {
      if (anilla.length < 3) continue;
      d += anilla
        .map(([lng, lat], i) => `${i === 0 ? 'M' : 'L'}${px(lng).toFixed(1)} ${py(lat).toFixed(1)}`)
        .join('');
      d += 'Z';
    }
  }
  return d;
}

/** Agrupa puntos casi coincidentes para no apilar círculos idénticos. */
function agrupar(puntos: PuntoMapa[]) {
  const rejilla = new Map<string, { lat: number; lng: number; n: number; zona?: string }>();
  for (const p of puntos) {
    // ~0.02° ≈ 2 km: dos solicitudes del mismo barrio son un solo punto.
    const k = `${Math.round(p.lat / 0.02)}:${Math.round(p.lng / 0.02)}`;
    const g = rejilla.get(k);
    if (g) { g.n += 1; } else { rejilla.set(k, { lat: p.lat, lng: p.lng, n: 1, zona: p.zona }); }
  }
  return [...rejilla.values()].sort((a, b) => a.n - b.n); // los mayores encima
}

export function MapaOrigen({ contornos, puntos, fueraDeCuadro }: {
  contornos: Contorno[];
  puntos: PuntoMapa[];
  /** Cuántas solicitudes cayeron fuera del encuadre (otro país). */
  fueraDeCuadro: number;
}) {
  const trazos = useMemo(
    () => contornos.map((c) => {
      // El rótulo va sobre el polígono MAYOR: en las provincias con islas, el
      // centroide del conjunto cae en el mar.
      const mayor = c.p.reduce(
        (mej, poly) => (areaAnilla(poly[0]) > areaAnilla(mej[0]) ? poly : mej),
        c.p[0],
      );
      const [lng, lat] = POSICION_ROTULO[c.n] ?? centroideArea(mayor[0]);
      return { n: c.n, d: trazaContorno(c.p), x: px(lng), y: py(lat) };
    }),
    [contornos],
  );
  const grupos = useMemo(
    () => agrupar(puntos.filter((p) => dentroDeCaja(p.lat, p.lng))),
    [puntos],
  );
  const maxN = Math.max(1, ...grupos.map((g) => g.n));

  return (
    <div className={s.caja}>
      <svg className={s.mapa} viewBox={`0 0 ${ANCHO} ${ALTO}`} role="img"
        aria-label="Mapa de Panamá con el origen de las solicitudes de teleconsulta">
        <g className={s.tierra}>
          {trazos.map((t) => (
            <path key={t.n} d={t.d} className={s.provincia}>
              <title>{t.n}</title>
            </path>
          ))}
        </g>
        {/* Los nombres van DEBAJO de los puntos: son contexto del mapa, no el
            dato. El halo del color del panel los despega del relleno. */}
        <g className={s.rotulos}>
          {trazos.map((t) => (
            <text key={t.n} x={t.x} y={t.y} className={s.rotulo}>{t.n}</text>
          ))}
        </g>
        <g>
          {grupos.map((g, i) => {
            // El radio crece con la raíz del conteo: el ÁREA queda proporcional
            // al número de solicitudes, que es como el ojo compara círculos.
            const r = 5 + 13 * Math.sqrt(g.n / maxN);
            return (
              <g key={i} className={s.punto}>
                <circle cx={px(g.lng)} cy={py(g.lat)} r={r} className={s.halo} />
                <circle cx={px(g.lng)} cy={py(g.lat)} r={r * 0.42} className={s.nucleo}>
                  <title>{`${g.zona ?? 'Ubicación'}: ${g.n} solicitud${g.n === 1 ? '' : 'es'}`}</title>
                </circle>
              </g>
            );
          })}
        </g>
      </svg>

      {grupos.length === 0 && (
        <span className={s.vacio}>Sin ubicaciones capturadas en el período</span>
      )}
      {fueraDeCuadro > 0 && (
        <span className={s.fuera}>
          +{fueraDeCuadro} fuera de Panamá
        </span>
      )}
    </div>
  );
}
